import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Clock3, Zap, FileCheck2 } from "lucide-react";
import { useSEO } from "@/lib/seo";
import { MaskedLine, FadeIn, Reveal, Chapter } from "@/components/Reveal";
import PhoneMockup from "@/components/PhoneMockup";
import BrowserFrame from "@/components/BrowserFrame";

const LC_STATS = [
    { icon: Clock3, value: "20+ hrs", label: "saved per week" },
    { icon: Zap, value: "50%", label: "faster invoicing" },
    { icon: FileCheck2, value: "1 hr", label: "avg POD turnaround" },
];

const PSR = ({ items }) => (
    <div className="space-y-8">
        {items.map((it) => (
            <div key={it.label}>
                <p className={`text-xs font-semibold uppercase tracking-[0.25em] ${it.color}`}>{it.label}</p>
                <p className="mt-3 text-base leading-relaxed text-slate-600 md:text-lg">{it.text}</p>
            </div>
        ))}
    </div>
);

export default function Work() {
    useSEO(
        "Our Work | LoadCircle & ServiceNext — Digital Circle Calgary",
        "Case studies from Digital Circle: LoadCircle, a Canadian trucking TMS, and ServiceNext, a local services marketplace app. Problem, solution, result."
    );

    return (
        <main className="pt-32 md:pt-44" data-testid="work-page">
            <section className="mx-auto max-w-7xl px-5 md:px-8">
                <FadeIn>
                    <p className="flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.25em] text-slate-500">
                        <span className="ring-logo inline-block h-3.5 w-3.5" />
                        Our Work
                    </p>
                </FadeIn>
                <h1 className="mt-6 max-w-4xl text-5xl font-black tracking-tighter text-slate-900 md:text-7xl" data-testid="work-headline">
                    <MaskedLine delay={0.1}>Products we</MaskedLine>
                    <MaskedLine delay={0.22}>
                        <span className="gradient-text">stake our name on.</span>
                    </MaskedLine>
                </h1>
                <FadeIn delay={0.4} className="mt-6 max-w-xl">
                    <p className="text-base leading-relaxed text-slate-600 md:text-lg">
                        These aren't client projects we walked away from — they're our own products, running in the real world, right now.
                    </p>
                </FadeIn>
            </section>

            {/* ---------- LOADCIRCLE ---------- */}
            <section className="mx-auto max-w-7xl px-5 py-20 md:px-8 md:py-28" data-testid="case-loadcircle">
                <Chapter index="Case 01" label="LoadCircle — Web Platform" />
                <div className="mt-10 grid items-start gap-12 lg:grid-cols-5">
                    <div className="lg:col-span-2">
                        <Reveal>
                            <h2 className="text-3xl font-black tracking-tighter text-slate-900 md:text-4xl">
                                The trucking TMS that ends paperwork chaos.
                            </h2>
                        </Reveal>
                        <Reveal delay={0.1} className="mt-8">
                            <PSR
                                items={[
                                    { label: "Problem", color: "text-rose-500", text: "Canadian carriers and freight brokers were running fleets on paper, Excel, and six disconnected apps — lost PODs, slow invoicing, and zero visibility into which loads actually made money." },
                                    { label: "Solution", color: "text-blue-500", text: "We built LoadCircle: a Canadian-first TMS and carrier verification platform covering dispatch, driver app, POD uploads, fuel expenses, auto-generated statements, load-level P&L, and Samsara telematics integration." },
                                    { label: "Result", color: "text-emerald-500", text: "Carriers on LoadCircle cut POD turnaround from days to under an hour, invoice 50% faster, and save 20+ hours of admin every week — with setup in under an hour." },
                                ]}
                            />
                        </Reveal>
                        <Reveal delay={0.15} className="mt-10 grid grid-cols-3 gap-4">
                            {LC_STATS.map((s) => (
                                <div key={s.label} className="rounded-2xl border border-slate-200 bg-white p-4 text-center shadow-sm">
                                    <s.icon className="mx-auto h-4 w-4 text-blue-500" />
                                    <p className="mt-2 text-lg font-extrabold tracking-tight text-slate-900 md:text-xl">{s.value}</p>
                                    <p className="mt-1 text-[11px] leading-tight text-slate-500">{s.label}</p>
                                </div>
                            ))}
                        </Reveal>
                        <Reveal delay={0.2} className="mt-10">
                            <a
                                href="https://www.loadcircle.ca"
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="loadcircle-visit-link"
                                className="group inline-flex items-center gap-2 rounded-full bg-slate-900 px-8 py-4 text-sm font-semibold text-white transition-[transform,box-shadow,background-color] duration-300 hover:-translate-y-1 hover:bg-slate-800 hover:shadow-xl"
                            >
                                Visit loadcircle.ca
                                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                            </a>
                        </Reveal>
                    </div>

                    <div className="lg:col-span-3">
                        <Reveal delay={0.1}>
                            <div className="relative rounded-[2rem] bg-gradient-to-b from-slate-100 to-slate-50 p-5 md:p-8">
                                <div className="absolute -right-12 -top-12 h-48 w-48 rounded-full bg-blue-200/60 blur-3xl" aria-hidden="true" />
                                <BrowserFrame
                                    src="/assets/loadcircle/hero-dashboard.png"
                                    alt="LoadCircle dashboard showing live load tracking, driver status, and profitability metrics"
                                    url="app.loadcircle.ca — Dashboard"
                                    className="relative"
                                />
                            </div>
                        </Reveal>
                        <div className="mt-6 grid gap-6 sm:grid-cols-2">
                            <Reveal delay={0.15}>
                                <BrowserFrame
                                    src="/assets/loadcircle/pnl-dashboard.png"
                                    alt="LoadCircle P&L reporting dashboard with monthly profit, revenue, and expense breakdown"
                                    url="app.loadcircle.ca — P&L"
                                />
                            </Reveal>
                            <Reveal delay={0.2}>
                                <BrowserFrame
                                    src="/assets/loadcircle/fuel-management.png"
                                    alt="LoadCircle fuel cards and expense management table"
                                    url="app.loadcircle.ca — Fuel"
                                />
                            </Reveal>
                        </div>
                    </div>
                </div>
            </section>

            {/* ---------- SERVICENEXT ---------- */}
            <section className="bg-white py-20 md:py-28" data-testid="case-servicenext">
                <div className="mx-auto max-w-7xl px-5 md:px-8">
                    <Chapter index="Case 02" label="ServiceNext — Mobile App" />
                    <div className="mt-10 grid items-center gap-12 lg:grid-cols-5">
                        <div className="order-2 lg:order-1 lg:col-span-2">
                            <Reveal delay={0.1}>
                                <div className="relative overflow-hidden rounded-[2rem] bg-slate-950 px-6 py-12 md:px-10">
                                    <div className="absolute -left-16 -top-16 h-56 w-56 rounded-full bg-teal-500/25 blur-3xl" aria-hidden="true" />
                                    <div className="absolute -bottom-20 -right-10 h-56 w-56 rounded-full bg-purple-500/25 blur-3xl" aria-hidden="true" />
                                    <PhoneMockup className="relative" />
                                    <p className="relative mt-8 text-center text-[11px] uppercase tracking-[0.2em] text-slate-500">
                                        Placeholder render — real app screenshot coming soon
                                    </p>
                                </div>
                            </Reveal>
                        </div>
                        <div className="order-1 lg:order-2 lg:col-span-3">
                            <Reveal>
                                <h2 className="text-3xl font-black tracking-tighter text-slate-900 md:text-4xl">
                                    Verified local pros, one search away.
                                </h2>
                            </Reveal>
                            <Reveal delay={0.1} className="mt-8">
                                <PSR
                                    items={[
                                        { label: "Problem", color: "text-rose-500", text: "Finding a trustworthy plumber, electrician, or mover meant word-of-mouth luck and endless phone tag — no ratings, no accountability, no idea who would actually show up." },
                                        { label: "Solution", color: "text-teal-500", text: "We built ServiceNext: a mobile marketplace where customers search by service, browse providers on a list or map, and compare verified ratings, jobs completed, and response times — then chat or request directly in-app." },
                                        { label: "Result", color: "text-emerald-500", text: "Customers book verified local pros in minutes instead of days, and service providers get a steady stream of qualified local jobs with zero marketing spend." },
                                    ]}
                                />
                            </Reveal>
                            <Reveal delay={0.15} className="mt-10 flex flex-wrap gap-3">
                                {["iOS & Android", "List + Map search", "Verified providers", "In-app chat"].map((t) => (
                                    <span key={t} className="rounded-full border border-slate-200 bg-slate-50 px-4 py-2 text-xs font-semibold text-slate-600">
                                        {t}
                                    </span>
                                ))}
                            </Reveal>
                        </div>
                    </div>
                </div>
            </section>

            {/* ---------- NEXT CTA ---------- */}
            <section className="mx-auto max-w-7xl px-5 py-20 md:px-8 md:py-28" data-testid="work-next-cta">
                <Reveal>
                    <div className="relative overflow-hidden rounded-[2.5rem] bg-slate-950 px-8 py-16 md:px-16 md:py-24">
                        <div className="conic-ring spin-slow absolute -left-32 -bottom-32 h-[420px] w-[420px] opacity-30 blur-2xl" aria-hidden="true" />
                        <div className="relative flex flex-col items-start justify-between gap-10 lg:flex-row lg:items-end">
                            <div>
                                <p className="text-xs font-semibold uppercase tracking-[0.25em] text-slate-400">Case 03 — unwritten</p>
                                <h2 className="mt-4 max-w-2xl text-4xl font-black tracking-tighter text-white md:text-6xl">
                                    Your project could be <span className="gradient-text">next.</span>
                                </h2>
                                <p className="mt-5 max-w-xl text-base leading-relaxed text-slate-400">
                                    We take on a small number of client builds at a time — so yours gets the same care we give our own products.
                                </p>
                            </div>
                            <Link
                                to="/contact"
                                data-testid="work-quote-button"
                                className="group inline-flex shrink-0 items-center gap-2 rounded-full bg-white px-8 py-4 text-sm font-semibold text-slate-900 transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-2xl"
                            >
                                Start the conversation
                                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                            </Link>
                        </div>
                    </div>
                </Reveal>
            </section>
        </main>
    );
}
